import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  User, Package, MapPin, Heart, LifeBuoy, LogOut, Trash2, ShieldCheck,
  MessageCircle, ShoppingCart, ChevronDown, KeyRound, Plus, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { validCPF } from "@/pages/CheckoutPage";
import { OrderTimeline } from "@/components/store/OrderTimeline";
import { ProductCard } from "@/components/store/ProductCard";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const onlyDigits = (v) => (v || "").replace(/\D/g, "");
const maskCPF = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskPhone = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
const maskCEP = (v) => onlyDigits(v).slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const STATUS_PILL = {
  aguardando_confirmacao: ["Aguardando confirmação", "bg-amber-50 text-amber-700"],
  confirmado: ["Confirmado", "bg-blue-50 text-brand"],
  em_separacao: ["Em separação", "bg-violet-50 text-violet-700"],
  saiu_para_entrega: ["Saiu para entrega", "bg-indigo-50 text-indigo-700"],
  entregue: ["Entregue", "bg-green-50 text-green-700"],
  cancelamento_solicitado: ["Cancelamento em análise", "bg-orange-50 text-orange-700"],
  cancelado: ["Cancelado", "bg-red-50 text-red-600"],
};

const TICKET_TYPES = {
  assistencia: "Assistência técnica",
  devolucao: "Devolução",
  garantia: "Garantia",
  arrependimento: "Direito de arrependimento",
  outro: "Outro assunto",
};
const TICKET_STATUS = {
  aberto: ["Aberto", "bg-amber-50 text-amber-700"],
  em_atendimento: ["Em atendimento", "bg-blue-50 text-brand"],
  resolvido: ["Resolvido", "bg-green-50 text-green-700"],
};

// ============================= autenticação =============================
const AuthView = () => {
  const { login, register } = useCustomer();
  const [mode, setMode] = useState("login"); // login | register | forgot | reset
  const [form, setForm] = useState({ name: "", cpf: "", email: "", phone: "", password: "" });
  const [resetCode, setResetCode] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const r = await login(form.email, form.password);
        if (!r.ok) toast.error(r.error);
      } else if (mode === "register") {
        if (!validCPF(form.cpf)) {
          toast.error("CPF inválido.");
          return;
        }
        const r = await register({
          name: form.name, cpf: onlyDigits(form.cpf), email: form.email,
          phone: onlyDigits(form.phone), password: form.password,
        });
        if (!r.ok) toast.error(r.error);
        else toast.success("Conta criada! Bem-vindo(a) à HG Móveis.");
      } else if (mode === "forgot") {
        await api.post("/account/forgot-password", { email: form.email });
        toast.success("Se este e-mail tiver uma conta, enviamos o código de redefinição.");
        setMode("reset");
      } else {
        await api.post("/account/reset-password", {
          email: form.email, code: onlyDigits(resetCode), password: form.password,
        });
        toast.success("Senha redefinida! Entre com a nova senha.");
        setMode("login");
        setForm((f) => ({ ...f, password: "" }));
        setResetCode("");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível concluir. Tente novamente."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="account-auth">
      <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8">
        <div className="flex rounded-full bg-gray-100 p-1 mb-6">
          {[["login", "Entrar"], ["register", "Criar conta"]].map(([m, label]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
                mode === m ? "bg-brand text-white" : "text-gray-500"
              }`}
              data-testid={`account-tab-${m}`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <div>
                <Label>Nome completo *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="register-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>CPF *</Label>
                  <Input inputMode="numeric" value={form.cpf} onChange={(e) => set("cpf", maskCPF(e.target.value))} placeholder="000.000.000-00" className="mt-1.5" data-testid="register-cpf" />
                </div>
                <div>
                  <Label>Telefone/WhatsApp *</Label>
                  <Input inputMode="tel" value={form.phone} onChange={(e) => set("phone", maskPhone(e.target.value))} placeholder="(21) 99999-9999" className="mt-1.5" data-testid="register-phone" />
                </div>
              </div>
            </>
          )}
          {(mode === "login" || mode === "register" || mode === "forgot" || mode === "reset") && (
            <div>
              <Label>E-mail *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="account-email" />
            </div>
          )}
          {mode === "reset" && (
            <div>
              <Label>Código recebido por e-mail *</Label>
              <Input inputMode="numeric" value={resetCode} onChange={(e) => setResetCode(onlyDigits(e.target.value).slice(0, 6))} placeholder="6 dígitos" className="mt-1.5 text-center text-lg tracking-[0.4em] font-bold" data-testid="reset-code" />
            </div>
          )}
          {mode !== "forgot" && (
            <div>
              <Label>{mode === "reset" ? "Nova senha *" : "Senha *"}</Label>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" data-testid="account-password" />
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 font-semibold" data-testid="account-submit">
            {loading ? "Aguarde..." : mode === "login" ? "Entrar" : mode === "register" ? "Criar minha conta" : mode === "forgot" ? "Enviar código" : "Redefinir senha"}
          </Button>
        </form>

        {mode === "login" && (
          <button type="button" onClick={() => setMode("forgot")} className="mt-4 text-sm text-brand font-medium hover:underline" data-testid="account-forgot">
            Esqueci minha senha
          </button>
        )}
        {(mode === "forgot" || mode === "reset") && (
          <button type="button" onClick={() => setMode("login")} className="mt-4 text-sm text-brand font-medium hover:underline" data-testid="account-back-login">
            Voltar para o login
          </button>
        )}
      </div>
      <p className="mt-4 text-xs text-gray-400 text-center flex items-center justify-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Seus dados são protegidos e nunca compartilhados.
      </p>
      <p className="mt-2 text-xs text-gray-500 text-center">
        Comprou sem conta? <Link to="/pedidos" className="text-brand font-medium hover:underline" data-testid="account-goto-cpf">Acompanhe pelo CPF</Link>
      </p>
    </div>
  );
};

// ============================= aba: pedidos =============================
const OrdersTab = () => {
  const { addItem } = useCart();
  const settings = useSettings();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [supportOrder, setSupportOrder] = useState(null);

  const load = () => api.get("/account/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => {
    load();
  }, []);

  const buyAgain = async (order) => {
    let added = 0;
    for (const i of order.items) {
      try {
        const { data: p } = await api.get(`/products/${i.product_id || ""}`);
        addItem(p, i.color || null, i.qty || 1);
        added++;
      } catch {
        /* produto fora do ar: pulado */
      }
    }
    if (added > 0) {
      toast.success("Itens adicionados ao carrinho!");
      navigate("/carrinho");
    } else {
      toast.error("Esses produtos não estão disponíveis no momento.");
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      await api.post("/account/orders/cancel", { order_id: cancelTarget.id, reason: cancelReason.trim() });
      toast.success(cancelTarget.status === "aguardando_confirmacao" ? "Pedido cancelado." : "Solicitação registrada! A equipe vai analisar.");
      setCancelTarget(null);
      setCancelReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  };

  if (orders === null) return <div className="animate-pulse h-40 bg-gray-50 rounded-2xl" />;
  if (orders.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center" data-testid="orders-empty">
        <Package className="w-10 h-10 mx-auto text-gray-200" />
        <p className="mt-2 text-sm text-gray-500">Você ainda não tem pedidos.</p>
        <Link to="/produtos">
          <Button className="mt-4 rounded-full" data-testid="orders-shop">Conhecer ofertas</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="account-orders">
      {orders.map((o) => {
        const pill = STATUS_PILL[o.status] || [o.status_label, "bg-gray-100 text-gray-600"];
        const open = openId === o.order_number;
        const canCancel = ["aguardando_confirmacao", "confirmado", "em_separacao", "saiu_para_entrega"].includes(o.status);
        return (
          <div key={o.order_number} className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid={`account-order-${o.order_number}`}>
            <button type="button" onClick={() => setOpenId(open ? null : o.order_number)} className="w-full p-4 sm:p-5 flex items-center gap-4 text-left hover:bg-gray-50/60 transition-colors" data-testid={`account-order-open-${o.order_number}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-gray-900">{o.order_number}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${pill[1]}`}>{pill[0]}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{fmtDateTime(o.created_at)} • {fmtBRL(o.total)}</p>
              </div>
              {o.items[0]?.image && <img src={imgVariant(o.items[0].image, "thumb")} alt="" loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />}
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <div className="px-4 sm:px-5 pb-5 border-t border-gray-100">
                {!["cancelado", "cancelamento_solicitado"].includes(o.status) ? (
                  <div className="mt-5"><OrderTimeline status={o.status} /></div>
                ) : (
                  <div className={`mt-4 rounded-xl p-3.5 text-sm ${o.status === "cancelado" ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-800"}`}>
                    {o.status === "cancelado" ? "Este pedido foi cancelado." : "Sua solicitação de cancelamento está em análise pela equipe."}
                  </div>
                )}
                <ul className="mt-5 space-y-3">
                  {o.items.map((i, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-sm">
                      {i.image && <img src={imgVariant(i.image, "thumb")} alt="" loading="lazy" decoding="async" className="w-11 h-11 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800 font-medium line-clamp-1">{i.name}</p>
                        <p className="text-xs text-gray-500">{i.qty}x{i.color ? ` • Cor: ${i.color}` : ""}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-gray-100 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Total</span><span className="font-bold">{fmtBRL(o.total)}</span></div>
                  <p className="text-xs text-gray-500">{o.payment_label}{o.installments > 1 ? ` em ${o.installments}x` : ""} • Entrega desejada: {fmtDate(o.delivery_date)}</p>
                  <p className="text-xs text-gray-500">{o.delivery_address}</p>
                </div>
                {o.status_history?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">Etapas do pedido</p>
                    <ul className="space-y-1">
                      {o.status_history.map((h, i) => (
                        <li key={i} className="text-xs text-gray-500 flex justify-between gap-3">
                          <span>{STATUS_PILL[h.status]?.[0] || h.status}</span>
                          <span className="shrink-0">{fmtDateTime(h.at)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-5 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => buyAgain(o)} data-testid={`account-buy-again-${o.order_number}`}>
                    <ShoppingCart className="w-4 h-4" /> Comprar novamente
                  </Button>
                  {canCancel && (
                    <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setCancelTarget(o)} data-testid={`account-cancel-${o.order_number}`}>
                      {o.status === "aguardando_confirmacao" ? "Cancelar pedido" : "Solicitar cancelamento"}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSupportOrder(o)} data-testid={`account-support-${o.order_number}`}>
                    <LifeBuoy className="w-4 h-4" /> Solicitar assistência
                  </Button>
                  <a href={buildWhatsAppUrl(`Olá! Sobre meu pedido ${o.order_number}...`, settings.whatsapp_number)} target="_blank" rel="noreferrer">
                    <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700" data-testid={`account-whatsapp-${o.order_number}`}>
                      <MessageCircle className="w-4 h-4" /> Falar com a loja
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* cancelamento */}
      <Dialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <DialogContent className="max-w-md" data-testid="account-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{cancelTarget?.status === "aguardando_confirmacao" ? "Cancelar pedido" : "Solicitar cancelamento"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">{cancelTarget?.order_number} — {fmtBRL(cancelTarget?.total)}</p>
            <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Motivo (opcional)" data-testid="account-cancel-reason" />
            <p className="text-xs text-gray-500">
              {cancelTarget?.status === "aguardando_confirmacao"
                ? "O cancelamento é imediato e não pode ser desfeito."
                : "A equipe analisará a solicitação. Compras online têm direito de arrependimento em até 7 dias após o recebimento (CDC, art. 49)."}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setCancelTarget(null)}>Voltar</Button>
              <Button onClick={doCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700" data-testid="account-cancel-confirm">
                {cancelling ? "Enviando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* assistência vinculada ao pedido */}
      <SupportDialog order={supportOrder} onClose={() => setSupportOrder(null)} onSaved={load} />
    </div>
  );
};

// ============================= assistência =============================
const SupportDialog = ({ order, onClose, onSaved }) => {
  const [type, setType] = useState("assistencia");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setType("assistencia");
    setMessage("");
  }, [order]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/account/support", { order_id: order?.id || null, type, message });
      toast.success("Solicitação registrada! Acompanhe na aba Assistência.");
      onClose();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!order} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle>Solicitar assistência {order?.order_number ? `— ${order.order_number}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label>Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="mt-1.5" data-testid="support-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_TYPES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Descreva o que aconteceu</Label>
            <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5" placeholder="Ex: o produto chegou com defeito em..." data-testid="support-message" />
          </div>
          <p className="text-xs text-gray-500">Se precisar, envie fotos pelo WhatsApp da loja após registrar a solicitação — elas serão anexadas ao seu atendimento.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose}>Voltar</Button>
            <Button onClick={save} disabled={saving || !message.trim()} data-testid="support-submit">
              {saving ? "Enviando..." : "Registrar solicitação"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SupportTab = () => {
  const [tickets, setTickets] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const load = () => api.get("/account/support").then((r) => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-4" data-testid="account-support-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Suas solicitações de assistência, devolução e garantia</p>
        <Button size="sm" className="rounded-full" onClick={() => setNewOpen(true)} data-testid="support-new">
          <Plus className="w-4 h-4" /> Nova solicitação
        </Button>
      </div>
      {tickets === null ? (
        <div className="animate-pulse h-32 bg-gray-50 rounded-2xl" />
      ) : tickets.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-500">
          Nenhuma solicitação registrada.
        </div>
      ) : (
        tickets.map((t) => {
          const st = TICKET_STATUS[t.status] || [t.status, "bg-gray-100 text-gray-600"];
          return (
            <div key={t.id} className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 text-sm">{TICKET_TYPES[t.type] || t.type}</span>
                <span className={`text-[10px] font-bold uppercase rounded-full px-2.5 py-0.5 ${st[1]}`}>{st[0]}</span>
                {t.order_number && <span className="text-xs text-gray-400">pedido {t.order_number}</span>}
              </div>
              <p className="text-sm text-gray-600 mt-2">{t.message}</p>
              <p className="text-xs text-gray-400 mt-2">Aberta em {fmtDateTime(t.created_at)}</p>
              {t.history?.length > 1 && (
                <ul className="mt-2 space-y-1">
                  {t.history.slice(1).map((h, i) => (
                    <li key={i} className="text-xs text-gray-500">
                      {TICKET_STATUS[h.status]?.[0] || h.status} — {fmtDateTime(h.at)}{h.note ? `: ${h.note}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })
      )}
      <SupportDialog order={newOpen ? {} : null} onClose={() => setNewOpen(false)} onSaved={load} />
    </div>
  );
};

// ============================= aba: dados =============================
const ProfileTab = () => {
  const { customer, setCustomer, logout } = useCustomer();
  const navigate = useNavigate();
  const [name, setName] = useState(customer?.name || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/account/me", { name, phone: onlyDigits(phone) });
      setCustomer(data);
      toast.success("Dados atualizados!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/account/delete", { password });
      toast.success(data.detail || "Conta excluída.");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível excluir."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 sm:p-6 space-y-4" data-testid="account-profile">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Nome completo</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="profile-name" />
        </div>
        <div>
          <Label>Telefone/WhatsApp</Label>
          <Input inputMode="tel" value={maskPhone(phone)} onChange={(e) => setPhone(e.target.value)} className="mt-1.5" data-testid="profile-phone" />
        </div>
        <div>
          <Label>E-mail (não editável)</Label>
          <Input value={customer?.email || ""} disabled className="mt-1.5 bg-gray-50" data-testid="profile-email" />
        </div>
        <div>
          <Label>CPF</Label>
          <Input value={customer?.cpf_masked || ""} disabled className="mt-1.5 bg-gray-50" data-testid="profile-cpf" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="rounded-full" data-testid="profile-save">
        {saving ? "Salvando..." : "Salvar dados"}
      </Button>

      <div className="pt-4 border-t border-gray-100">
        <p className="text-sm font-semibold text-gray-800">Excluir minha conta</p>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          Seus dados pessoais (nome, CPF, e-mail, telefone e endereços) serão removidos da conta. Os registros fiscais
          dos pedidos são preservados conforme a legislação (LGPD). Essa ação é definitiva.
        </p>
        <Button variant="outline" size="sm" className="mt-2 rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="account-delete-open">
          <Trash2 className="w-4 h-4" /> Solicitar exclusão da conta
        </Button>
      </div>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="account-delete-dialog">
          <DialogHeader>
            <DialogTitle>Excluir conta definitivamente?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">Confirme com sua senha. Os dados pessoais serão removidos e você perderá o acesso.</p>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Sua senha" data-testid="account-delete-password" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>Voltar</Button>
              <Button onClick={doDelete} disabled={deleting || !password} className="bg-red-600 hover:bg-red-700" data-testid="account-delete-confirm">
                {deleting ? "Excluindo..." : "Excluir definitivamente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================= aba: endereços =============================
const EMPTY_ADDR = { label: "Casa", cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "", reference: "", is_default: false };

const AddressesTab = () => {
  const [addresses, setAddresses] = useState(null);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [adding, setAdding] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => setAddresses([]));
  useEffect(() => {
    load();
  }, []);

  const lookupCep = async (cepValue) => {
    const cep = onlyDigits(cepValue);
    if (cep.length !== 8) return;
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (!data.erro) {
        setForm((f) => ({ ...f, street: data.logradouro || f.street, neighborhood: data.bairro || f.neighborhood, city: data.localidade || f.city, state: data.uf || f.state }));
      } else {
        toast.error("CEP não encontrado. Preencha o endereço manualmente.");
      }
    } catch {
      toast.error("Não foi possível consultar o CEP. Preencha manualmente.");
    }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/account/addresses", { ...form, cep: onlyDigits(form.cep) });
      setAddresses(data);
      setForm(EMPTY_ADDR);
      setAdding(false);
      toast.success("Endereço salvo!");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    const { data } = await api.delete(`/account/addresses/${id}`);
    setAddresses(data);
    toast.success("Endereço removido.");
  };

  const makeDefault = async (id) => {
    const { data } = await api.post(`/account/addresses/${id}/default`);
    setAddresses(data);
    toast.success("Endereço padrão atualizado.");
  };

  return (
    <div className="space-y-4" data-testid="account-addresses">
      {addresses === null ? (
        <div className="animate-pulse h-32 bg-gray-50 rounded-2xl" />
      ) : (
        <>
          {addresses.length === 0 && !adding && (
            <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-500">
              Nenhum endereço cadastrado.
            </div>
          )}
          {addresses.map((a) => (
            <div key={a.id} className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 flex items-start justify-between gap-3" data-testid={`address-${a.id}`}>
              <div className="text-sm">
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-brand" /> {a.label}
                  {a.is_default && <span className="text-[10px] font-bold uppercase bg-blue-50 text-brand rounded-full px-2 py-0.5">Padrão</span>}
                </p>
                <p className="text-gray-600 mt-1">{a.street}, {a.number}{a.complement ? ` — ${a.complement}` : ""}</p>
                <p className="text-xs text-gray-500">{a.neighborhood} — {a.city}/{a.state} • CEP {a.cep}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                {!a.is_default && (
                  <button onClick={() => makeDefault(a.id)} className="text-xs text-brand font-medium hover:underline" data-testid={`address-default-${a.id}`}>Tornar padrão</button>
                )}
                <button onClick={() => remove(a.id)} className="text-xs text-red-500 font-medium hover:underline" data-testid={`address-remove-${a.id}`}>Remover</button>
              </div>
            </div>
          ))}
          {!adding ? (
            <Button variant="outline" className="rounded-full" onClick={() => setAdding(true)} data-testid="address-add-open">
              <Plus className="w-4 h-4" /> Adicionar endereço
            </Button>
          ) : (
            <form onSubmit={add} className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3" data-testid="address-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome do endereço</Label>
                  <Input value={form.label} onChange={(e) => set("label", e.target.value)} placeholder="Casa, Trabalho..." className="mt-1.5" data-testid="address-label" />
                </div>
                <div>
                  <Label>CEP *</Label>
                  <Input inputMode="numeric" value={form.cep} onChange={(e) => set("cep", maskCEP(e.target.value))} onBlur={(e) => lookupCep(e.target.value)} placeholder="00000-000" className="mt-1.5" data-testid="address-cep" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Rua *</Label>
                  <Input value={form.street} onChange={(e) => set("street", e.target.value)} className="mt-1.5" data-testid="address-street" />
                </div>
                <div>
                  <Label>Número *</Label>
                  <Input value={form.number} onChange={(e) => set("number", e.target.value)} className="mt-1.5" data-testid="address-number" />
                </div>
                <div>
                  <Label>Complemento</Label>
                  <Input value={form.complement} onChange={(e) => set("complement", e.target.value)} className="mt-1.5" data-testid="address-complement" />
                </div>
                <div>
                  <Label>Bairro *</Label>
                  <Input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="mt-1.5" data-testid="address-neighborhood" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label>Cidade *</Label>
                    <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5" data-testid="address-city" />
                  </div>
                  <div>
                    <Label>UF *</Label>
                    <Input value={form.state} maxLength={2} onChange={(e) => set("state", e.target.value.toUpperCase())} className="mt-1.5" data-testid="address-state" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="rounded-full" data-testid="address-save">Salvar endereço</Button>
                <Button type="button" variant="outline" className="rounded-full" onClick={() => setAdding(false)}>Cancelar</Button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
};

// ============================= aba: favoritos =============================
const FavoritesTab = () => {
  const { customer, favorites } = useCustomer();
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (customer) {
        const { data } = await api.get("/account/favorites");
        setProducts(data);
      } else if (favorites.length) {
        const results = await Promise.all(
          favorites.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
        );
        setProducts(results.filter(Boolean));
      } else {
        setProducts([]);
      }
    };
    load();
  }, [customer, favorites]);

  return (
    <div data-testid="account-favorites">
      {products === null ? (
        <div className="animate-pulse h-40 bg-gray-50 rounded-2xl" />
      ) : products.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center">
          <Heart className="w-10 h-10 mx-auto text-gray-200" />
          <p className="mt-2 text-sm text-gray-500">Nenhum favorito ainda. Toque no coração dos produtos para guardar aqui.</p>
          <Link to="/produtos">
            <Button className="mt-4 rounded-full" data-testid="favorites-shop">Explorar produtos</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================= página =============================
const TABS = [
  { id: "pedidos", label: "Meus pedidos", icon: Package },
  { id: "dados", label: "Meus dados", icon: User },
  { id: "enderecos", label: "Endereços", icon: MapPin },
  { id: "favoritos", label: "Favoritos", icon: Heart },
  { id: "assistencia", label: "Assistência", icon: LifeBuoy },
];

export default function AccountPage() {
  const { customer, logout } = useCustomer();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "pedidos");

  if (customer === null) {
    return (
      <div className="container-store py-16 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="container-store py-8 sm:py-12" data-testid="account-page">
      <Seo title="Minha Conta | HG Móveis" noindex />
      {!customer ? (
        <>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight text-center">Minha Conta</h1>
          <p className="text-sm text-gray-500 mt-1 text-center mb-8">
            Entre ou crie sua conta para acompanhar pedidos, favoritos e endereços. Comprar sem conta continua disponível.
          </p>
          <AuthView />
        </>
      ) : (
        <div className="grid lg:grid-cols-[240px_1fr] gap-8 items-start">
          <aside className="bg-white border border-gray-100 rounded-2xl p-4 lg:sticky lg:top-40">
            <div className="flex items-center gap-3 px-2 pb-3 border-b border-gray-100">
              <span className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-heading font-bold">
                {customer.name?.[0]?.toUpperCase() || "C"}
              </span>
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate" data-testid="account-user-name">{customer.name}</p>
                <p className="text-xs text-gray-400 truncate">{customer.email}</p>
              </div>
            </div>
            <nav className="mt-3 flex lg:flex-col gap-1 overflow-x-auto">
              {TABS.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                      tab === t.id ? "bg-brand text-white" : "text-gray-600 hover:bg-blue-50 hover:text-brand"
                    }`}
                    data-testid={`account-tab-${t.id}`}
                  >
                    <Icon className="w-4 h-4" /> {t.label}
                  </button>
                );
              })}
              <button
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 whitespace-nowrap"
                data-testid="account-logout"
              >
                <LogOut className="w-4 h-4" /> Sair
              </button>
            </nav>
          </aside>
          <div>
            {tab === "pedidos" && <OrdersTab />}
            {tab === "dados" && <ProfileTab />}
            {tab === "enderecos" && <AddressesTab />}
            {tab === "favoritos" && <FavoritesTab />}
            {tab === "assistencia" && <SupportTab />}
          </div>
        </div>
      )}
    </div>
  );
}
